import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Building2, Handshake, Gift, LifeBuoy, DollarSign, ShieldCheck, UserCog } from "lucide-react";
import api from "@/lib/api";
import { Page, Spinner } from "@/components/shared";

export default function Overview() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setD(data)); }, []);
  if (!d) return <Spinner label="Loading platform overview…" />;

  const cards = [
    { icon: Users, label: "Total Users", value: d.total_users, color: "var(--cs-magenta)" },
    { icon: Building2, label: "Bands", value: d.bands, color: "var(--cs-blue)" },
    { icon: Handshake, label: "Brand Partners", value: d.users_by_role.brand_partner, color: "var(--cs-green)" },
    { icon: Gift, label: "Benefits", value: d.benefits, color: "var(--cs-amber)" },
    { icon: LifeBuoy, label: "Open Help Requests", value: d.help_open, color: d.help_open > 0 ? "var(--cs-red)" : "var(--cs-green)" },
    { icon: DollarSign, label: "Benefit Value Redeemed", value: `$${d.benefit_value_redeemed.toLocaleString()}`, color: "var(--cs-green)" },
  ];
  const roleRows = [
    { icon: Users, label: "Masqueraders", value: d.users_by_role.masquerader },
    { icon: ShieldCheck, label: "Band Leaders", value: d.users_by_role.band_leader },
    { icon: Handshake, label: "Brand Partners", value: d.users_by_role.brand_partner },
    { icon: UserCog, label: "Admins", value: d.users_by_role.admin },
  ];

  return (
    <Page>
      <h1 className="font-head font-extrabold text-3xl">Platform Overview</h1>
      <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>Everything across Carnival Safety 101</p>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {cards.map((c) => (
          <div key={c.label} data-testid={`admin-metric-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="cs-card p-5">
            <c.icon size={22} style={{ color: c.color }} />
            <p className="font-head font-extrabold text-3xl mt-3">{c.value}</p>
            <p className="text-xs mt-1" style={{ color: "var(--cs-fg2)" }}>{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <div className="cs-card p-5">
          <h2 className="font-head font-bold text-lg mb-4">Users by Role</h2>
          <div className="space-y-2">
            {roleRows.map((r) => (
              <div key={r.label} className="flex items-center justify-between rounded-xl p-3" style={{ background: "var(--cs-card-hi)" }}>
                <span className="flex items-center gap-2 text-sm font-semibold"><r.icon size={17} style={{ color: "var(--cs-magenta)" }} /> {r.label}</span>
                <span className="font-head font-bold">{r.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="cs-card p-5">
          <h2 className="font-head font-bold text-lg mb-4">Bands at a Glance</h2>
          <div className="space-y-2">
            {d.band_rows.map((b) => (
              <button key={b.id} data-testid={`admin-band-glance-${b.id}`} onClick={() => nav("/admin/bands")}
                className="cs-tap w-full text-left rounded-xl p-3 flex items-center justify-between" style={{ background: "var(--cs-card-hi)" }}>
                <div>
                  <p className="font-semibold text-sm">{b.name} <span className="font-mono text-xs" style={{ color: "var(--cs-magenta)" }}>{b.code}</span></p>
                  <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>{b.participants} participants</p>
                </div>
                <span className="text-[11px] rounded-full px-2 py-0.5 font-semibold" style={{ background: b.active ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)", color: b.active ? "var(--cs-green)" : "var(--cs-red)" }}>
                  {b.active ? (b.road_day_active ? "Road Day" : "Active") : "Suspended"}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Page>
  );
}
